import React, { useContext, useState } from "react";
import "./UploadPhoto.css";

import CheckIcon from "assets/Check.icon";
import UploadIcon from "assets/Upload.icon";
import TrashCan from "assets/TrashCan.icon";
import { Language, TText } from "api/type";
import { LanguageContext } from "contexts";

interface UploadPhotoProps {
  poseUrl: string;
  storePhoto: (imageBlob: Blob | null) => void;
  message?: string;
}

const text: TText = {
  uploadPhoto: {
    en: "Upload Photo",
    es: "Subir foto",
  },
  completed: {
    en: "Completed!",
    es: "Completado!",
  },
  errorMessage: {
    en: "Please upload a PNG, JPEG, or JPG file.",
    es: "Por favor sube un archivo PNG, JPEG, o JPG.",
  },
};

const UploadPhoto: React.FC<UploadPhotoProps> = ({
  poseUrl,
  storePhoto,
  message,
}) => {
  const language = useContext<Language>(LanguageContext)[0];
  const [picture, setPicture] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  if (!language) return null;

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setErrorMessage("");
    const file = event.target.files?.[0];

    if (file) {
      const allowedExtensions = /(\.png|\.jpeg|\.jpg)$/i;
      if (!allowedExtensions.exec(file.name)) {
        setErrorMessage(text.errorMessage[language]);
        return;
      }
      const blob = new Blob([file], { type: file.type });
      storePhoto(blob);
      setPicture(URL.createObjectURL(blob));
    }
  };

  const resetPicture = () => {
    setPicture(null);
    storePhoto(null);
  };

  return (
    <div style={styles.container}>
      {message && <span style={styles.message}>{message}</span>}
      <div className={`upload-button ${picture ? "upload-button--solid" : ""}`}>
        <div
          className={`upload-button__left ${
            picture ? "upload-button--uploaded" : ""
          }`}
        >
          {errorMessage && <p style={styles.errorText}>{errorMessage}</p>}
          {!errorMessage && picture && (
            <>
              <img
                className={`upload-button__img ${
                  picture ? "upload-button__img--hover" : ""
                }`}
                src={picture}
                alt=""
              />
              <div className="upload-button__trashcan" onClick={resetPicture}>
                <TrashCan />
              </div>
            </>
          )}
          {!errorMessage && !picture && (
            <img className="upload-button__img" src={poseUrl} alt="" />
          )}
        </div>
        <div
          className={`upload-button__right ${
            picture ? "upload-button--solid" : ""
          }`}
        >
          {picture ? (
            <>
              <CheckIcon />
              <p className="upload-button__text">{text.completed[language]}</p>
            </>
          ) : (
            <>
              <UploadIcon />
              <p className="upload-button__text">
                {text.uploadPhoto[language]}
              </p>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="upload-button__input"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
  },
  message: {
    display: "block",
    textAlign: "left",
    fontSize: "1rem",
    marginLeft: "0.75rem",
    marginRight: "1rem",
    marginBottom: "1.5rem",
    fontWeight: 400,
  },
  errorText: {
    color: "red",
  },
} as const;

export default UploadPhoto;
